import React, { useState, useEffect } from "react";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import { getUserCredits } from "../../api/businesses";

interface FormFillingProps {
  onProcessPhoto: (images: File[]) => void;
  isProcessing: boolean;
}

const FormFilling: React.FC<FormFillingProps> = ({
  onProcessPhoto,
  isProcessing,
}) => {
  const [image, setImage] = useState<File | null>(null);
  const [remainingCredits, setRemainingCredits] = useState<number | null>(null);

  // Messages to show in sequence while processing
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const loadingMessages = [
    "Reading your image...",
    "Removing furniture...",
    "Please wait, almost done...",
  ];

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isProcessing) {
      // Advance to the next message every 10 seconds, but do NOT loop back
      interval = setInterval(() => {
        setLoadingMessageIndex((prevIndex) => {
          if (prevIndex < loadingMessages.length - 1) {
            return prevIndex + 1;
          }
          return prevIndex; // Stay on the last message
        });
      }, 10000);
    } else {
      // Reset index when not processing
      setLoadingMessageIndex(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing, loadingMessages.length]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setImage(event.target.files[0]);
    }
  };

  const handleClearImage = () => setImage(null);

  const handleProcess = async () => {
    if (!image) {
      alert("Please upload an image.");
      return;
    }

    try {
      // Fetch the user's remaining credits
      const res = await getUserCredits();
      const credits = res.data.credits;

      if (credits <= 0) {
        alert(
          "You don't have enough credits to process a photo. Please add more credits."
        );
        return;
      }

      // Update remaining credits and proceed
      setRemainingCredits(credits);
      onProcessPhoto([image]);
    } catch (error) {
      console.error("Error fetching user credits:", error);
      alert("Failed to fetch your credits. Please try again later.");
    }
  };

  return (
    <div className="w-full max-w-md bg-white p-4 rounded-lg shadow-lg border">
      {/* Message Section */}
      <div className="mb-4 text-center bg-blue-100 text-blue-600 p-2 rounded">
        <p>Please upload an image to remove furniture.</p>
      </div>

      <div className="w-full h-64 border-2 border-dashed rounded-lg mb-4 flex justify-center items-center relative">
        {image ? (
          <div className="relative w-full h-full">
            <img
              src={URL.createObjectURL(image)}
              alt="Uploaded"
              className="w-full h-full object-cover"
            />
            <button
              onClick={handleClearImage}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600"
            >
              <DeleteIcon fontSize="small" />
            </button>
          </div>
        ) : (
          <label htmlFor="upload" className="text-center cursor-pointer">
            <UploadFileIcon fontSize="large" />
            <span>Upload Image</span>
          </label>
        )}
        <input
          id="upload"
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />
      </div>
      <button
        onClick={handleProcess}
        disabled={!image || isProcessing}
        className={`w-full py-2 ${
          image && !isProcessing
            ? "bg-blue-500 text-white"
            : "bg-gray-300 text-gray-500"
        } rounded-lg`}
      >
        {isProcessing ? (
          <div className="flex items-center justify-center">
            <CircularProgress size={20} color="inherit" />
            <span className="ml-2">{loadingMessages[loadingMessageIndex]}</span>
          </div>
        ) : (
          "Process Photo"
        )}
      </button>
    </div>
  );
};

export default FormFilling;
